import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { HubConnection } from '@microsoft/signalr';
import { ConfigService } from './config.service';

@Injectable({
    providedIn: 'root'
})
export class SignalRReferralsService {
    private _hubConnection: HubConnection | undefined;
    private _hubSubscriptionList: HubSubscription[] = [];
    private _hubSubscriptionProcessingQueue: HubSubscriptionAction[] = [];
    private _processingLocked = false;

    constructor(private readonly configService: ConfigService) {}

    /**
     * If a HubConnection exists but is disconnected, re-start it. Otherwise if not exist, create one, and return it.
     * @returns a HubConnection
     */
    async getHubConnection() {
        if (!this._hubConnection) {
            try {
                const hubUrl = new URL('/api/hubs/entity-events', this.configService.getSettings().apiBaseUrl).toString();
                this._hubConnection = this.createHubConnection(hubUrl);
                await this._hubConnection.start();
            } catch (error) {
                console.warn(error);
                throw error;
            }
        } else if (this._hubConnection.state === signalR.HubConnectionState.Disconnected) {
            // The Connection can become Disconnected if user is offline. And if user is back online and
            // switched to a different component, we want to start the Connection before returning the _hubConnection for further use.
            await this._hubConnection.start();
        }
        return this._hubConnection;
    }

    /**
     * Create a HubConnection with given Uri, and attach the access token to the HubConnection.
     * @param hubUri
     * @returns a HubConnection
     */
    createHubConnection(hubUri: string): HubConnection {
        const hubConnection = new signalR.HubConnectionBuilder().withUrl(hubUri).withAutomaticReconnect().build();

        hubConnection.onreconnecting(error => {
            console.warn(error);
        });

        hubConnection.onreconnected(() => {
            this._onHubReconnected();
        });

        return hubConnection;
    }

    /**
     * Gets called when Hub is reconnected. Re-subscribe all the events and functions that are stored in _hubSubscriptionList.
     */
    private async _onHubReconnected() {
        try {
            const hubConnection = await this.getHubConnection();
            this._hubSubscriptionList.forEach(async sub => {
                await this._startSingleSubscription(hubConnection, sub);
            });
        } catch (e) {
            console.warn(e);
        }
    }

    /**
     * A method for starting an individual subscription on server.
     * @param {HubConnection} hubConnection The existing HubConnection for reuse.
     * @param {HubSubscription} sub The subscription to be applied on server.
     */
    private async _startSingleSubscription(hubConnection: HubConnection, sub: HubSubscription) {
        sub.eventFnPair.forEach(pair => {
            hubConnection.on(pair.eventName, pair.eventCallbackFn);
        });
        await hubConnection.invoke(sub.subsciptionMethodName, sub.orgCode, sub.siteIds);
    }

    /**
     * Start an individual HubSubscription, and add to _hubSubscriptionList
     * @param newSub The HubSubscription to be started and added to list.
     */
    private async _addToHubSubscription(newSub: HubSubscription | undefined) {
        try {
            if (newSub) {
                const hubConnection = await this.getHubConnection();
                await this._startSingleSubscription(hubConnection, newSub);
                this._hubSubscriptionList.push(newSub);
            }
        } catch (e) {
            console.warn(e);
        }
    }

    /**
     * Remove a given HubSubscription from the subscription list, and if no other subscriptions
     * with same group exist, unsubscribe from server.
     * @param {string} subIdToRemove The exact HubSubscription to be removed.
     */
    private async _removeFromHubSubscription(subIdToRemove: string | undefined) {
        try {
            // unsubscribe locally
            const index = this._hubSubscriptionList.findIndex(existingSub => existingSub.id === subIdToRemove);
            if (index > -1) {
                const subToRemove = this._hubSubscriptionList[index];
                this._hubSubscriptionList.splice(index, 1);
                const hubConnection = await this.getHubConnection();
                subToRemove.eventFnPair.forEach(pair => {
                    hubConnection.off(pair.eventName, pair.eventCallbackFn);
                });
                // unsubscribe from server if no existing subscription with same group
                const isOtherSubWithSameOrgAndSite = this._hubSubscriptionList.some(
                    existingSub =>
                        existingSub.subsciptionMethodName === subToRemove.subsciptionMethodName &&
                        existingSub.orgCode === subToRemove.orgCode &&
                        this.areArraysEqual(existingSub.siteIds, subToRemove.siteIds)
                );
                if (!isOtherSubWithSameOrgAndSite) {
                    await hubConnection.invoke(subToRemove.unsubscriptionMethodName, subToRemove.orgCode, subToRemove.siteIds);
                }
            }
        } catch (e) {
            console.warn(e);
        }
    }

    /**
     * Add a HubSubscriptionAction to _hubSubscriptionProcessingQueue and process the list.
     * If _processingLocked is true, it means the list is being processed.
     * In this case, only add the action to the queue, and it will be processed in sequence.
     * @param action a HubSubscriptionAction to be added to queue and processed
     */
    async addAction(action: HubSubscriptionAction) {
        this._hubSubscriptionProcessingQueue.push(action);
        if (!this._processingLocked) {
            this._processingLocked = true;
            await this._processAction();
        }
    }

    /**
     * If _hubSubscriptionProcessingQueue is not empty, start processing and set _processingLocked to true.
     * It dispatches the action to either _addToHubSubscription() or _removeFromHubSubscription() depending on action.isUnsub.
     * It unlocks after the queue is empty.
     */
    private async _processAction() {
        while (this._hubSubscriptionProcessingQueue.length) {
            const currentAction = this._hubSubscriptionProcessingQueue.shift() as HubSubscriptionAction;
            if (currentAction.isUnsub) {
                await this._removeFromHubSubscription(currentAction.hubSubscriptionId);
            } else {
                await this._addToHubSubscription(currentAction.hubSubscription);
            }
        }
        this._processingLocked = false;
    }

    private areArraysEqual(array1: unknown[] | undefined, array2: unknown[] | undefined): boolean {
        return array1 === array2 || (array1?.length === array2?.length && array1?.every((value, index) => value === array2?.[index]));
    }
}

export class HubSubscriptionAction {
    isUnsub: boolean;
    hubSubscription?: HubSubscription;
    hubSubscriptionId?: string;

    constructor(isUnsub: boolean, hubSubscription: HubSubscription | undefined, hubSubscriptionId: string | undefined) {
        this.isUnsub = isUnsub;
        this.hubSubscription = hubSubscription;
        this.hubSubscriptionId = hubSubscriptionId;
    }
}

/**
 * A class for each individual subscription for Hub.
 */
export class HubSubscription {
    id: string;
    orgCode: string | undefined;
    siteIds: number[] | undefined;
    subsciptionMethodName: string;
    unsubscriptionMethodName: string;
    eventFnPair: HubSubscriptionEventFnPair[];

    constructor(
        id: string,
        orgCode: string | undefined,
        siteIds: number[] | undefined,
        subsciptionMethodName: string,
        unsubsciptionMethodName: string,
        eventFnPair: HubSubscriptionEventFnPair[]
    ) {
        this.id = id;
        this.orgCode = orgCode;
        this.siteIds = siteIds;
        this.subsciptionMethodName = subsciptionMethodName;
        this.unsubscriptionMethodName = unsubsciptionMethodName;
        this.eventFnPair = eventFnPair;
    }
}

export class HubSubscriptionEventFnPair {
    eventName: string;
    eventCallbackFn: (...parameters: unknown[]) => void;

    constructor(
        eventName: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        eventCallbackFn: (...parameters: any[]) => void
    ) {
        this.eventName = eventName;
        this.eventCallbackFn = eventCallbackFn;
    }
}
